<template>
  <div class="p-6 dark:text-gray-50">
    <h1 class="text-3xl font-bold text-gray-900 mb-6 dark:text-white">Log Details</h1>
    <div v-if="log" class="bg-white dark:bg-gray-700 shadow overflow-hidden sm:rounded-lg">
      <div class="px-4 py-5 sm:px-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-50">
          Log ID: {{ log.id }}
        </h3>
        <p class="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-50">
          Added on: {{ formatDateTime(log.added_on) }}
        </p>
      </div>
      <div class="border-t border-gray-200">
        <dl>
          <div
            v-for="(value, key) in logDetails"
            :key="key"
            :class="{ 'bg-gray-50 dark:bg-gray-800': key % 2 === 0, 'dark:bg-gray-800 bg-white': key % 2 !== 0 }"
            class="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
          >
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-50">
              {{ formatKey(key) }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-gray-50 sm:mt-0 sm:col-span-2">
              <template v-if="['headers', 'body', 'response'].includes(key)">
                <pre class="bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-x-auto">{{
                  formatValue(value)
                }}</pre>
              </template>
              <template v-else-if="key === 'status_code'">
                <span :class="getStatusCodeClass(value)">{{ value }}</span>
              </template>
              <template v-else>
                {{ formatValue(value) }}
              </template>
            </dd>
          </div>
        </dl>
      </div>
    </div>
    <div v-else class="text-center py-10">
      <p class="text-xl text-gray-600">Loading log details...</p>
    </div>
    <div class="mt-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-200"
      >
        Back to Logs
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getLogDetails } from "@/services/logService";

export default {
  name: "LogDetails",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const log = ref(null);

    const logDetails = computed(() => {
      if (!log.value) return {};
      return {
        api: log.value.api,
        method: log.value.method,
        status_code: log.value.status_code,
        execution_time: `${log.value.execution_time} s`,
        client_ip_address: log.value.client_ip_address,
        headers: log.value.headers,
        body: log.value.body,
        response: log.value.response,
      };
    });

    const fetchLogDetails = async () => {
      try {
        const logId = route.params.id;
        log.value = await getLogDetails(logId);
      } catch (error) {
        console.error("Error fetching log details:", error);
        // Handle error (e.g., show error message to user)
      }
    };

    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const formatValue = (value) => {
      if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
      }
      return value;
    };

    const formatKey = (key) => {
      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const getStatusCodeClass = (statusCode) => {
      if (statusCode >= 200 && statusCode < 300) return "text-green-600";
      if (statusCode >= 400 && statusCode < 500) return "text-yellow-600";
      if (statusCode >= 500) return "text-red-600";
      return "text-gray-600";
    };

    const goBack = () => {
      router.push({ name: "Logs" });
    };

    onMounted(fetchLogDetails);

    return {
      log,
      logDetails,
      formatDateTime,
      formatValue,
      formatKey,
      getStatusCodeClass,
      goBack,
    };
  },
};
</script>
